import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createNursingNote,
  getNursingNoteById,
  listNursingNotes,
  searchNursingStaff,
  searchNursingTemplates,
  updateNursingNote,
} from "../../services/nursingNotesService";

const initialState = {
  patient: null,
  records: [],
  templates: [],
  staff: [],
  editingId: null,
  loading: false,
  saving: false,
  lookupsLoading: false,
  error: null,
};

export const fetchNursingTemplates = createAsyncThunk(
  "nursingNotes/fetchTemplates",
  async (query, { rejectWithValue }) => {
    try {
      return await searchNursingTemplates(query);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchNursingStaff = createAsyncThunk(
  "nursingNotes/fetchStaff",
  async (query, { rejectWithValue }) => {
    try {
      return await searchNursingStaff(query);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchNursingNotesList = createAsyncThunk(
  "nursingNotes/fetchList",
  async (params, { rejectWithValue }) => {
    try {
      return await listNursingNotes(params);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchNursingNoteById = createAsyncThunk(
  "nursingNotes/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      return await getNursingNoteById(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveNursingNote = createAsyncThunk(
  "nursingNotes/save",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      if (id) {
        return await updateNursingNote(id, payload);
      }
      return await createNursingNote(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const nursingNotesSlice = createSlice({
  name: "nursingNotes",
  initialState,
  reducers: {
    setPatient: (state, action) => {
      state.patient = action.payload;
      state.records = [];
      state.editingId = null;
    },
    clearEditing: (state) => {
      state.editingId = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNursingTemplates.pending, (state) => {
        state.lookupsLoading = true;
      })
      .addCase(fetchNursingTemplates.fulfilled, (state, action) => {
        state.lookupsLoading = false;
        state.templates = action.payload || [];
      })
      .addCase(fetchNursingTemplates.rejected, (state, action) => {
        state.lookupsLoading = false;
        state.templates = [];
        state.error = action.payload || "Failed to load templates";
      })
      .addCase(fetchNursingStaff.pending, (state) => {
        state.lookupsLoading = true;
      })
      .addCase(fetchNursingStaff.fulfilled, (state, action) => {
        state.lookupsLoading = false;
        state.staff = action.payload || [];
      })
      .addCase(fetchNursingStaff.rejected, (state, action) => {
        state.lookupsLoading = false;
        state.staff = [];
        state.error = action.payload || "Failed to load staff";
      })
      .addCase(fetchNursingNotesList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNursingNotesList.fulfilled, (state, action) => {
        state.loading = false;
        state.records = action.payload || [];
      })
      .addCase(fetchNursingNotesList.rejected, (state, action) => {
        state.loading = false;
        state.records = [];
        state.error = action.payload || "Failed to load notes";
      })
      .addCase(fetchNursingNoteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNursingNoteById.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.nursingRmoNotesId) {
          state.editingId = action.payload.nursingRmoNotesId;
        }
      })
      .addCase(fetchNursingNoteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load note";
      })
      .addCase(saveNursingNote.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveNursingNote.fulfilled, (state) => {
        state.saving = false;
        state.editingId = null;
      })
      .addCase(saveNursingNote.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to save note";
      });
  },
});

export const { setPatient, clearEditing, clearError } = nursingNotesSlice.actions;

export default nursingNotesSlice.reducer;
